// Global Instructions Rule Applied!
import dayjs from 'dayjs'
import { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FormTable } from '../FormTable/index.tsx'
import { uploadFile, deleteFile, listFiles, getSignedUrl } from '../../services/filesService.ts'
import { addTab } from '../../../store/slices/tabSlice'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../utils/errorToast.tsx'
import {
  FILE_TYPE,
  URL_TYPE,
  OFFICE_VIEWER_URL,
  OFFICE_EXTENSIONS,
  DATE_FORMAT
} from '../../../state_manager/constants/files.ts'
import { transformFileData, type TransformedFileData } from './utils/fileTransformers.ts'
import type { RootState } from '../../../state_manager/store.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'

import FileTableColumns from './parts/FileTableColumns.tsx'
import FileActions from './parts/FileActions.tsx'
import RefreshButton from './parts/RefreshButton.tsx'
import UploadModal from './parts/UploadModal.tsx'

interface FileRecord {
  key: string
  name: string
  type: typeof FILE_TYPE | typeof URL_TYPE
  uploadDate: string
  file_path: string | null
  url: string | null
}

interface FileUploadManagerProps {
  files?: TransformedFileData[]
  onFileUpload?: ((file: TransformedFileData) => void) | null
  onRefreshRequest?: ((refreshFn: () => Promise<void>) => void) | null
  tableName?: string | null
  idField?: string | null
  idValue?: string | null
}

function FileUploadManagerComponent({
  files = [],
  onFileUpload = null,
  onRefreshRequest = null,
  tableName = null,
  idField = null,
  idValue = null
}: FileUploadManagerProps) {
  const dispatch = useDispatch<AppDispatch>()
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken)
  const [modalVisible, setModalVisible] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState<TransformedFileData[]>([])
  const [deletingRowKey, setDeletingRowKey] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [loading, setLoading] = useState(false)
  const refreshRef = useRef<(() => Promise<void>) | null>(null)

  useEffect(() => {
    const fetchFiles = async () => {
      if (idValue && accessToken) {
        try {
          setLoading(true)
          const filesList = await listFiles(idValue, accessToken, null, tableName || null, idField || null)
          const transformedFiles = filesList.map(transformFileData)
          setUploadedFiles(transformedFiles)
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to fetch files'
          showErrorToast(errorMessage)
        } finally {
          setLoading(false)
        }
      } else if (files.length > 0) {
        setUploadedFiles(files)
      }
    }

    fetchFiles()
  }, [idValue, accessToken, tableName, idField, files])

  const handleRefresh = async () => {
    if (idValue && accessToken) {
      try {
        setLoading(true)
        const filesList = await listFiles(idValue, accessToken, null, tableName || null, idField || null)
        const transformedFiles = filesList.map(transformFileData)
        setUploadedFiles(transformedFiles)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to fetch files'
        showErrorToast(errorMessage)
      } finally {
        setLoading(false)
      }
    } else if (files.length > 0) {
      setUploadedFiles(files)
    }
  }

  useEffect(() => {
    refreshRef.current = handleRefresh
    if (onRefreshRequest) {
      onRefreshRequest(handleRefresh)
    }
    // eslint-disable-next-line
  }, [onRefreshRequest])

  const handleAddClick = () => {
    setModalVisible(true)
  }

  const handleModalCancel = () => {
    setModalVisible(false)
  }

  const isMicrosoftOfficeFile = (fileName: string): boolean => {
    if (!fileName || typeof fileName !== 'string') return false
    const extension = fileName.toLowerCase().split('.').pop()
    return extension ? OFFICE_EXTENSIONS.includes(extension) : false
  }

  const handleFileClick = async (filePathOrUrl: string, fileName: string, isUrl = false) => {
    if (!filePathOrUrl) return

    try {
      let urlToOpen: string

      if (isUrl) {
        urlToOpen = filePathOrUrl
      } else {
        const signedUrlResponse = await getSignedUrl(filePathOrUrl, accessToken)
        urlToOpen = signedUrlResponse.signed_url
      }

      if (isMicrosoftOfficeFile(fileName)) {
        urlToOpen = `${OFFICE_VIEWER_URL}${encodeURIComponent(urlToOpen)}`
      }

      const tabKey = `file-viewer-${filePathOrUrl.replace(/[^a-zA-Z0-9]/g, '-')}-${Date.now()}`
      const tabTitle = fileName || 'File Viewer'

      dispatch(
        addTab({
          id: tabKey,
          key: tabKey,
          title: tabTitle,
          path: `/file-viewer/${tabKey}`,
          type: 'file-viewer',
          module: 'file-viewer',
          closeable: true,
          fileUrl: urlToOpen,
          fileName: fileName
        })
      )
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to open file'
      showErrorToast(errorMessage)
    }
  }

  const uploadSingleFile = async (file: File): Promise<TransformedFileData> => {
    const uploadResponse = await uploadFile(
      file,
      accessToken,
      idValue,
      null,
      FILE_TYPE,
      undefined,
      undefined,
      tableName,
      idField
    )
    // Post-2026-05-28: upload response carries the file_urls_* row id.
    // Use it as the React key + as the file_url_id for delete. Falling
    // back to file_path keeps any edge-case dev environment that hasn't
    // redeployed the backend from crashing — unique within the list.
    return {
      id: uploadResponse.id || uploadResponse.file_path,
      type: FILE_TYPE,
      name: uploadResponse.file_name,
      file_path: uploadResponse.file_path,
      uploadDate: dayjs(uploadResponse.uploaded_at).format(DATE_FORMAT)
    }
  }

  const uploadFiles = async (filesToUpload: File[]): Promise<TransformedFileData[]> => {
    const uploadedFilesList: TransformedFileData[] = []

    for (const file of filesToUpload) {
      try {
        const newFile = await uploadSingleFile(file)
        uploadedFilesList.push(newFile)
        if (onFileUpload) {
          onFileUpload(newFile)
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to upload file'
        showErrorToast(errorMessage)
      }
    }

    if (uploadedFilesList.length === filesToUpload.length && uploadedFilesList.length > 0) {
      toast.success('File(s) uploaded successfully')
    } else {
      showErrorToast('File(s) upload failed')
    }

    return uploadedFilesList
  }

  const uploadUrl = async (url: string, fileName: string): Promise<TransformedFileData> => {
    const uploadResponse = await uploadFile(
      null,
      accessToken,
      idValue,
      null,
      URL_TYPE,
      url,
      fileName,
      tableName,
      idField
    )

    return {
      id: uploadResponse.id || uploadResponse.file_path,
      type: URL_TYPE,
      name: uploadResponse.file_name,
      file_path: uploadResponse.file_path || uploadResponse.public_url,
      url: uploadResponse.public_url || uploadResponse.file_path,
      uploadDate: uploadResponse.uploaded_at
        ? dayjs(uploadResponse.uploaded_at).format(DATE_FORMAT)
        : dayjs().format(DATE_FORMAT)
    }
  }

  const handleUpload = async (uploadData: {
    type: typeof FILE_TYPE | typeof URL_TYPE
    files?: File[]
    url?: string
    fileName?: string
  }) => {
    try {
      setUploading(true)

      if (uploadData.type === FILE_TYPE && uploadData.files && uploadData.files.length > 0) {
        const uploadedFilesList = await uploadFiles(uploadData.files)
        if (uploadedFilesList.length > 0) {
          setUploadedFiles((prev) => [...prev, ...uploadedFilesList])
        }
      } else if (uploadData.type === URL_TYPE && uploadData.url && uploadData.fileName) {
        try {
          const newFile = await uploadUrl(uploadData.url, uploadData.fileName)
          setUploadedFiles((prev) => [...prev, newFile])
          if (onFileUpload) {
            onFileUpload(newFile)
          }
          toast.success('URL added successfully')
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : 'Failed to add URL'
          showErrorToast(errorMessage)
        }
      }

      setModalVisible(false)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload files'
      showErrorToast(errorMessage)
    } finally {
      setUploading(false)
    }
  }

  const handleRemove = async (key: string) => {
    try {
      setDeletingRowKey(key)

      const fileToDelete = uploadedFiles.find((file) => file.id === key)

      if (fileToDelete) {
        // Both URL-type and Storage-type attachments are row-backed
        // (post-2026-05-28). Delete by file_url_id (the row UUID). The
        // backend handles the type distinction internally — Storage
        // rows get moved to _trash/{YYYY-MM-DD}/{row_id}/{filename};
        // URL rows just get trashed=true.
        await deleteFile(null, accessToken, fileToDelete.id, tableName || null)
        toast.success(
          fileToDelete.type === URL_TYPE
            ? 'URL deleted successfully'
            : 'File deleted successfully'
        )
      }

      setUploadedFiles((prev) => prev.filter((file) => file.id !== key))
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete file'
      showErrorToast(errorMessage)
    } finally {
      setDeletingRowKey(null)
    }
  }

  const dataSource: FileRecord[] = uploadedFiles.map((file) => ({
    key: file.id,
    name: file.name || file.url || 'Untitled',
    type: file.type || FILE_TYPE,
    uploadDate: file.uploadDate || '',
    file_path: file.file_path || null,
    url: file.url || null
  }))

  const columns = FileTableColumns({ onFileClick: handleFileClick })

  const customActionsRenderer = (_: unknown, record: FileRecord) => {
    return <FileActions record={record} deletingRowKey={deletingRowKey} onRemove={handleRemove} />
  }

  const headerActions = <RefreshButton loading={loading} onRefresh={handleRefresh} />

  return (
    <>
      <FormTable
        title='Files'
        dataSource={dataSource}
        columns={columns}
        addButtonText=''
        addButtonIcon={faPlus}
        emptyText='No files uploaded'
        onAdd={handleAddClick}
        isAddingNew={false}
        showHeader={true}
        loading={loading}
        headerActions={headerActions}
        includeActions={true}
        actionsWidth={60}
        deletingRowKey={deletingRowKey}
        onRemove={handleRemove}
        useDeleteConfirm={false}
        customActionsRenderer={customActionsRenderer}
        cardProps={{
          size: 'small',
          type: 'inner',
          styles: { body: { padding: 10 } }
        }}
      />

      <UploadModal visible={modalVisible} onCancel={handleModalCancel} onUpload={handleUpload} loading={uploading} />
    </>
  )
}

export const FileUploadManager = FileUploadManagerComponent
